import { useMsal } from "@azure/msal-react";
import { ChevronDown, LogOut } from "lucide-react";
import { signOut } from "@/services/authService";
import {
  headerButtonClassName,
  headerMenuEnterStyle,
} from "@/components/layout/headerButtonClassName";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { APP_VERSION } from "@/lib/env";
import { useHeaderMenuFocus } from "@/hooks/useHeaderMenuFocus";

interface AuthStatusProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const namePartsFrom = (displayName: string) => {
  const nameParts = displayName.split(" ").filter(Boolean);
  const firstName = nameParts[0] ?? "";
  const lastNameInitial =
    nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] : undefined;

  return { firstName, lastNameInitial };
};

const renderAccountEmail = (email: string | undefined) => {
  if (!email) {
    return null;
  }

  return (
    <span className="truncate text-sm text-muted-foreground">{email}</span>
  );
};

export const AuthStatus = ({ open, onOpenChange }: AuthStatusProps) => {
  const { accounts, inProgress } = useMsal();
  const { notePointerOpen, noteKeyboardOpen, keepTriggerFocused } =
    useHeaderMenuFocus();

  if (accounts.length > 0) {
    const account = accounts[0];
    const username = account.username ?? "";
    const displayName = account.name?.trim() || username;
    const email = username && username !== displayName ? username : undefined;
    const { firstName, lastNameInitial } = namePartsFrom(displayName);
    const chevronClassName = open
      ? "size-4 rotate-180 transition-transform"
      : "size-4 transition-transform";
    const logoutInProgress = inProgress === "logout";

    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={`Kontomenü von ${displayName}`}
            className={`border-transparent bg-transparent px-2 text-primary-foreground hover:cursor-pointer hover:bg-transparent hover:text-primary-foreground focus-visible:bg-transparent focus-visible:text-primary-foreground active:bg-transparent active:text-primary-foreground ${headerButtonClassName}`}
            onPointerDown={notePointerOpen}
            onKeyDown={noteKeyboardOpen}
          >
            <UserAvatar
              firstName={firstName}
              lastNameInitial={lastNameInitial}
              size="md"
              backgroundColor="var(--cream)"
            />
            <ChevronDown aria-hidden="true" className={chevronClassName} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          aria-label="Konto"
          className="w-[280px] rounded-xl p-2 data-[state=closed]:pointer-events-none"
          style={headerMenuEnterStyle}
          onOpenAutoFocus={keepTriggerFocused}
          onCloseAutoFocus={keepTriggerFocused}
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <UserAvatar
              firstName={firstName}
              lastNameInitial={lastNameInitial}
              size="md"
              backgroundColor="var(--cream)"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-bold text-primary">
                {displayName}
              </span>
              {renderAccountEmail(email)}
            </div>
          </div>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left font-semibold text-primary outline-none hover:bg-secondary-bright/15 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={signOut}
            disabled={logoutInProgress}
          >
            <LogOut aria-hidden="true" className="size-4 text-secondary" />
            Abmelden
          </button>
          <div className="mx-2 my-1 h-px bg-border" role="separator" />
          <p className="px-3 py-2 text-[10px] whitespace-nowrap tabular-nums text-muted-foreground">
            Version {APP_VERSION}
          </p>
        </PopoverContent>
      </Popover>
    );
  }

  return null;
};
